import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, ExternalLink, AlertTriangle, RefreshCw, Receipt, Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/billing`;
export const B = {
  ink: "#1A1A1A", ink2: "#59554D", ink3: "#8A857A", muted: "#B5AFA3",
  card: "#FFFFFF", panel: "#F6F4EF", border: "#DAD5CA", divider: "#EDE9E0",
  accent: "#FFD108", accentEdge: "#E0B800", accentInk: "#8A6D00",
  good: "#0E6B3F", bad: "#C23A00",
};
const MANROPE = { fontFamily: "Manrope, sans-serif" };
const FOCUS = "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFD108]";

export const fig = (m) => (m ? `${m.currency} ${Number(m.amount).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—");
export const ddmon = (iso) => (iso
  ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })
  : "—");

const STATUS_TONE = {
  paid: { color: B.ink2, background: B.panel }, open: { color: B.accentInk, background: "#FFF8D6" },
  draft: { color: B.ink3, background: B.panel }, void: { color: B.muted, background: B.panel },
  uncollectible: { color: B.bad, background: "#FFF1EC" },
  active: { color: B.good, background: "#EAF5EF" }, trialing: { color: B.accentInk, background: "#FFF8D6" },
  past_due: { color: B.bad, background: "#FFF1EC" }, canceled: { color: B.muted, background: B.panel },
  unpaid: { color: B.bad, background: "#FFF1EC" },
};
export const Pill = ({ value, loud, testid }) => (
  <span data-testid={testid} className="text-[10px] font-bold uppercase tracking-[0.07em] px-1.5 py-0.5 rounded"
    style={loud ? { color: B.bad, background: "#FFF1EC" } : STATUS_TONE[value] || { color: B.ink2, background: B.panel }}>
    {String(value || "—").replace(/_/g, " ")}
  </span>
);

const SEAT_KINDS = [["office", "office"], ["mobile", "mobile"],
  ["timesheet", "timesheet-only"], ["guardian", "Guardian"]];

const Row = ({ label, children }) => (  <div className="flex items-baseline gap-2 text-[12.5px]">
    <span className="text-[10px] font-bold uppercase tracking-[0.08em] w-[104px] shrink-0" style={{ color: B.ink3 }}>{label}</span>
    <span style={{ color: B.ink }}>{children}</span>
  </div>
);

function DocketInvoiceForm({ clientId, currency, onSent, onCancel }) {
  const [f, setF] = useState({ quantity: 10, unit_amount: "", days_until_due: 14, tax_free_export: false });
  const [busy, setBusy] = useState(false);
  const total = (Number(f.quantity) || 0) * (Number(f.unit_amount) || 0);
  const send = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/client/${encodeURIComponent(clientId)}/docket-invoice`, {
        quantity: Number(f.quantity), unit_amount: Number(f.unit_amount),
        days_until_due: Number(f.days_until_due), tax_free_export: f.tax_free_export,
      });
      if (r.data.draft) {
        toast.success("Raised as a draft in Stripe", {
          description: "Review it in Stripe, then finalise and send. Nothing has been emailed to the client.",
          duration: 8000,
        });
      } else {
        toast.success(`Invoice ${r.data.invoice.number || ""} sent — Stripe will email it`);
      }
      onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send that invoice"); }
    finally { setBusy(false); }
  };
  const input = (k, label, type = "number") => (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.08em] mb-1" style={{ color: B.ink3 }}>{label}</div>
      <input type={type} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
        data-testid={`billing-docket-${k}`}
        className={`w-full h-9 rounded-[7px] px-2.5 text-[13px] bg-white ${FOCUS}`}
        style={{ border: `1px solid ${B.border}`, color: B.ink }} />
    </div>
  );
  return (
    <div className="rounded-[8px] p-3 space-y-3" style={{ background: B.panel, border: `1px solid ${B.border}` }}
      data-testid="billing-docket-form">
      <div className="grid grid-cols-3 gap-2.5">
        {input("quantity", "Dockets")}
        {input("unit_amount", `Rate each (${currency})`)}
        {input("days_until_due", "Due in days")}
      </div>
      <label className="flex items-center gap-2 text-[12.5px]" style={{ color: B.ink2 }}>
        <input type="checkbox" checked={f.tax_free_export} data-testid="billing-docket-export"
          onChange={(e) => setF({ ...f, tax_free_export: e.target.checked })} />
        Tax-free export
      </label>
      <div className="flex items-center gap-2">
        <span className="mono text-[14px] font-bold" style={{ color: B.ink }} data-testid="billing-docket-total">
          {currency} {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
        <button onClick={send} disabled={busy || !total} data-testid="billing-docket-send"
          className={`ml-auto h-9 px-3.5 rounded-[7px] text-[11.5px] font-bold uppercase disabled:opacity-50 ${FOCUS}`}
          style={{ background: B.accent, border: `1px solid ${B.accentEdge}`, color: "#000" }}>
          {busy ? "Sending…" : "Send invoice"}
        </button>
        <button onClick={onCancel} data-testid="billing-docket-cancel"
          className={`h-9 px-3 rounded-[7px] text-[11.5px] font-bold uppercase ${FOCUS}`}
          style={{ background: B.card, border: `1px solid ${B.border}`, color: B.ink2 }}>Cancel</button>
      </div>
    </div>
  );
}

export default function BillingCard({ clientId, currency = "AUD" }) {
  const [d, setD] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [raising, setRaising] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/client/${encodeURIComponent(clientId)}`)
      .then((r) => setD(r.data))
      .catch((e) => setD({ error: e?.response?.status === 403 ? "forbidden" : "failed" }));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try { await axios.post(`${API}/sync`); toast.success("Read back from Stripe"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Stripe sync failed"); }
    finally { setSyncing(false); }
  };

  const shell = (body, action) => (
    <div className="rounded-[11px] overflow-hidden" style={{ background: B.card, border: `1px solid ${B.border}` }}
      data-testid="client-billing-card">
      <div className="px-3.5 py-2.5 flex items-center gap-2" style={{ borderBottom: `1px solid ${B.divider}` }}>
        <CreditCard className="w-4 h-4" style={{ color: B.ink3 }} />
        <span style={{ ...MANROPE, fontSize: 13.5, fontWeight: 700, color: B.ink }}>Billing</span>
        {action}
      </div>
      <div className="px-3.5 py-3 space-y-3">{body}</div>
    </div>
  );

  if (!d) return shell(<div className="text-[12.5px]" style={{ color: B.ink2 }}>Loading…</div>);
  if (d.error) {
    return shell(<div className="text-[12.5px]" style={{ color: B.ink2 }} data-testid="billing-no-access">
      {d.error === "forbidden" ? "You don't have access to billing figures." : "Billing could not be loaded."}
    </div>);
  }

  const refresh = (
    <button onClick={sync} disabled={syncing} data-testid="billing-sync"
      className={`ml-auto inline-flex items-center gap-1 text-[11px] font-bold uppercase ${FOCUS}`}
      style={{ color: B.accentInk, letterSpacing: "0.04em" }}>
      <RefreshCw className={`w-3 h-3 ${syncing ? "animate-spin" : ""}`} /> Refresh
    </button>
  );

  if (!d.set_up_to_pay) {
    return shell(
      <div className="text-[12.5px]" style={{ color: B.ink2 }} data-testid="billing-not-set-up">
        <span className="font-bold" style={{ color: B.ink }}>Not set up to pay.</span> {d.note}
      </div>, refresh);
  }

  const f = d.flags || {};
  const dockets = d.dockets || {};
  return shell(
    <>
      {(f.failed_payments > 0 || f.overdue_count > 0 || f.card_expiring) && (
        <div className="rounded-[8px] px-3 py-2.5 flex items-start gap-2 text-[12.5px]"
          style={{ background: "#FFF1EC", border: "1px solid #F0C4B4", color: "#8E3A12" }}
          data-testid="billing-warning">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            {f.failed_payments > 0 && <>{f.failed_payments} payment{f.failed_payments > 1 ? "s" : ""} failed. </>}
            {f.overdue_count > 0 && <>{f.overdue_count} invoice{f.overdue_count > 1 ? "s" : ""} overdue
              {f.worst_days_overdue ? ` — worst is ${f.worst_days_overdue} days late` : ""}. </>}
            {f.card_expiring && <>The card on file expires before the next renewal. </>}
            It's already a to-do on this client.
          </span>
        </div>
      )}

      {d.subscriptions.length === 0 ? (
        <div className="text-[12.5px]" style={{ color: B.ink2 }}>No subscription — invoiced only.</div>
      ) : d.subscriptions.map((s) => {
        // The stored amount is the sum of the prices attached. Where those are in
        // another currency than Stripe charges, it is not what the client pays.
        const unverified = (s.items || []).some((i) => i.amount_unverified);
        return (
        <div key={s.id} className="space-y-1.5" data-testid={`billing-sub-${s.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="mono text-[15px] font-bold"
              style={{ color: unverified ? B.ink3 : B.ink }}
              data-testid={`billing-sub-amount-${s.id}`}>
              {unverified
                ? `${(s.currency || "aud").toUpperCase()} —`
                : fig({ amount: (s.amount || 0) / 100, currency: (s.currency || "aud").toUpperCase() })}
            </span>
            <span className="text-[12px]" style={{ color: B.ink2 }}>/ {s.interval || "period"}</span>
            <Pill value={s.status} testid={`billing-sub-status-${s.id}`} />
            {s.cancel_at_period_end && <Pill value="cancelling" loud />}
          </div>
          <Row label="Plan">{(s.product_names || []).join(", ") || "—"}</Row>
          <Row label="Next charge">{ddmon(s.current_period_end)}</Row>
          <Row label="Pays by">
            {s.collection_method === "send_invoice" ? "Invoiced"
              : (d.payment_methods || []).length ? "Card on file" : "Direct debit / automatic"}
          </Row>
        </div>
        );
      })}

      {d.seats?.has_users && (
        <div data-testid="billing-seats">
          <div className="flex items-baseline gap-2 mb-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.08em]" style={{ color: B.ink3 }}>
              Users
            </span>
            <span className="mono text-[14px] font-bold" style={{ color: B.ink }}
              data-testid="billing-seats-total">{d.seats.total_users}</span>
            <span className="text-[12px]" style={{ color: B.ink2 }}>
              {SEAT_KINDS.filter(([k]) => d.seats.users?.[k])
                .map(([k, l]) => `${d.seats.users[k]} ${l}`).join(" · ")}
            </span>
          </div>
          {d.seats.included && (
            <div className="text-[11.5px] mb-1" style={{ color: B.ink3 }}>
              The plan includes {d.seats.included.office} office and {d.seats.included.mobile} mobile
              user{d.seats.included.mobile === 1 ? "" : "s"}; the rest are extras below.
            </div>
          )}
          {d.seats.lines.map((ln, i) => (
            <div key={i} className="flex items-baseline gap-2 py-0.5 text-[12px]"
              data-testid={`billing-seat-line-${i}`}>
              <span className="flex-1 min-w-0 truncate" style={{ color: B.ink }}>{ln.label}</span>
              <span className="mono text-right w-[30px] shrink-0" style={{ color: B.ink2 }}>×{ln.quantity}</span>
              <span className="mono text-right w-[76px] shrink-0 truncate" style={{ color: B.ink3 }}>{fig(ln.unit)}</span>
              <span className="mono text-right w-[80px] shrink-0 truncate"
                style={{ color: ln.unverified ? B.bad : B.ink }}>
                {ln.unverified ? "—" : fig(ln.total)}
              </span>
            </div>
          ))}
          <div className="flex items-baseline gap-2 pt-1 mt-0.5" style={{ borderTop: `1px solid ${B.divider}` }}>
            <span className="text-[11.5px]" style={{ color: B.ink3 }}>Total</span>
            <span className="mono text-[13px] font-bold ml-auto"
              style={{ color: d.seats.figures_unverified ? B.bad : B.ink }}
              data-testid="billing-seats-money">
              {d.seats.figures_unverified
                ? `Not confirmed in ${(d.seats.charge_currencies || []).join("/")}`
                : d.seats.totals.map(fig).join(" + ")}
            </span>
          </div>
          {d.seats.figures_unverified && (
            <div className="text-[11.5px] mt-1" style={{ color: B.bad }}
              data-testid="billing-seats-unverified">
              Stripe bills this customer in {(d.seats.charge_currencies || []).join("/")}, but the prices
              attached are in another currency. The user numbers are right; the amounts will fill in on
              the next Stripe read.
            </div>
          )}
          {d.seats.unrecognised?.length > 0 && (
            <div className="text-[11.5px] mt-1" style={{ color: B.ink3 }}
              data-testid="billing-seats-unrecognised">
              Not counted as users: {d.seats.unrecognised.join(", ")}.
            </div>
          )}
        </div>
      )}

      {(d.payment_methods || []).map((pm, i) => (
        <Row key={i} label="Card">
          <span className="mono">{(pm.brand || "card").toUpperCase()} •••• {pm.last4}</span>
          <span style={{ color: pm.expiring_before_renewal ? B.bad : B.ink3 }}> · expires {pm.expires}</span>
        </Row>
      ))}

      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[10px] font-bold uppercase tracking-[0.08em]" style={{ color: B.ink3 }}>
            Pay-Per-Docket
          </span>
          <button onClick={() => setRaising(!raising)} data-testid="billing-docket-new"
            className={`ml-auto inline-flex items-center gap-1 text-[11px] font-bold uppercase ${FOCUS}`}
            style={{ color: B.accentInk }}>
            <Plus className="w-3 h-3" /> Send docket invoice
          </button>
        </div>
        {raising && <DocketInvoiceForm clientId={clientId} currency={currency}
          onSent={() => { setRaising(false); load(); }} onCancel={() => setRaising(false)} />}
        {!raising && (
          <>
            <div className="text-[12.5px]" style={{ color: B.ink2 }} data-testid="billing-docket-spend">
              {dockets.purchases?.length
                ? <>{dockets.purchases.length} purchase{dockets.purchases.length > 1 ? "s" : ""} ·
                  spent {dockets.spend?.map(fig).join(" + ") || "—"}</>
                : "No docket purchases yet."}
            </div>
            <div className="text-[11.5px] mt-0.5" style={{ color: B.ink3 }} data-testid="billing-balance-note">
              {dockets.balance_connected ? "" : dockets.balance_note}
            </div>
          </>
        )}
      </div>

      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.08em] mb-1.5" style={{ color: B.ink3 }}>
          Invoices
        </div>
        {d.invoices.length === 0 ? (
          <div className="text-[12.5px]" style={{ color: B.ink2 }}>Nothing invoiced yet.</div>
        ) : d.invoices.slice(0, 8).map((i) => (
          <div key={i.id} className="grid gap-2 items-center py-1.5 text-[12px]"
            style={{ gridTemplateColumns: "92px 86px minmax(0,1fr) 92px 74px",
              borderBottom: `1px solid ${B.divider}` }} data-testid={`billing-invoice-${i.id}`}>
            <span className="mono truncate" style={{ color: B.ink2 }}>{i.number || i.id.slice(-8)}</span>
            <span className="mono" style={{ color: B.ink2 }}>{ddmon(i.created)}</span>
            <span className="truncate" style={{ color: B.ink }}>
              {i.description || (i.is_docket ? "Pay-Per-Docket" : "Subscription")}
              {i.days_overdue > 0 && <span style={{ color: B.bad }}> · {i.days_overdue}d late</span>}
            </span>
            <span className="mono text-right" style={{ color: B.ink }}>{fig(i.money)}</span>
            <span className="flex items-center justify-end gap-1">
              <Pill value={i.status} loud={i.failed} />
              {i.hosted_invoice_url && (
                <a href={i.hosted_invoice_url} target="_blank" rel="noreferrer" title="Open invoice"
                  data-testid={`billing-invoice-link-${i.id}`}>
                  <ExternalLink className="w-3 h-3" style={{ color: B.ink3 }} />
                </a>
              )}
            </span>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 pt-1">
        {(d.customers || []).map((c) => (
          <a key={c.id} href={c.dashboard_url} target="_blank" rel="noreferrer"
            data-testid={`billing-stripe-link-${c.id}`}
            className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase ${FOCUS}`}
            style={{ color: B.accentInk }}>
            <Receipt className="w-3 h-3" /> Open in Stripe
          </a>
        ))}
        <span className="ml-auto text-[11px]" style={{ color: B.muted }}>
          {d.settings?.last_sync_at ? `Read ${new Date(d.settings.last_sync_at).toLocaleString("en-AU")}` : ""}
        </span>
      </div>
    </>, refresh);
}
